import { Suspense, lazy, useEffect, useRef, useState, type ReactNode } from 'react';
import type { CharacterId } from '@audigoal/core';
import type { Emotion } from '@/characters/types';
import { cn } from '@/lib/cn';
import { useMotionPrefs } from '@/lib/use-motion-prefs';

/* ------------------------------------------------------------------ Badge */

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

const RARITY: Record<Rarity, string> = {
  common: 'bg-rarity-common/15 text-rarity-common border-rarity-common',
  uncommon: 'bg-rarity-uncommon/15 text-rarity-uncommon border-rarity-uncommon',
  rare: 'bg-rarity-rare/15 text-rarity-rare border-rarity-rare',
  epic: 'bg-rarity-epic/15 text-rarity-epic border-rarity-epic',
  legendary: 'bg-rarity-legendary/20 text-rarity-legendary border-rarity-legendary',
};

export function Badge({
  children,
  rarity = 'common',
  className,
}: {
  children: ReactNode;
  rarity?: Rarity;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-bold uppercase tracking-wide',
        RARITY[rarity],
        className,
      )}
    >
      {children}
    </span>
  );
}

/* ---------------------------------------------------------------- Counter */

export interface CounterProps {
  value: number;
  className?: string;
  /** Milliseconds for the full count-up. Ignored under reduced motion. */
  durationMs?: number;
}

/** Animated number used for XP, gems and scores. */
export function Counter({ value, className, durationMs = 800 }: CounterProps) {
  const { reduced } = useMotionPrefs();
  const [animated, setAnimated] = useState(value);
  const fromRef = useRef(value);

  useEffect(() => {
    // Under reduced motion the value is rendered directly, so there is nothing
    // to animate — just keep the start point in sync for a later change.
    if (reduced) {
      fromRef.current = value;
      return;
    }
    const from = fromRef.current;
    if (from === value) return;

    const start = performance.now();
    let raf = 0;
    const tick = (now: number): void => {
      const p = Math.min(1, (now - start) / durationMs);
      // ease-out cubic so the number settles rather than stopping dead
      const eased = 1 - Math.pow(1 - p, 3);
      setAnimated(Math.round(from + (value - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, durationMs, reduced]);

  const shown = reduced ? value : animated;
  return <span className={cn('display-num', className)}>{shown.toLocaleString()}</span>;
}

/* ----------------------------------------------------------- RingProgress */

export function RingProgress({
  value,
  size = 56,
  stroke = 6,
  label,
  children,
}: {
  value: number;
  size?: number;
  stroke?: number;
  label?: string;
  children?: ReactNode;
}) {
  const pct = Math.max(0, Math.min(1, value));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;

  return (
    <div className="relative inline-grid place-items-center" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        role={label ? 'img' : 'presentation'}
        aria-label={label}
        className="-rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--line)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--xp)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: 'stroke-dashoffset var(--dur-emphasis) var(--ease-out)' }}
        />
      </svg>
      <span className="absolute grid place-items-center">{children}</span>
    </div>
  );
}

/* --------------------------------------------------------------- Skeleton */

export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded-md bg-line', className)} />;
}

/* ------------------------------------------------------------- EmptyState */

/**
 * The rigs are 50 kB and `EmptyState` is reachable from the router's error
 * boundary, which is in the entry chunk. Loading them on demand keeps a screen
 * nobody has hit yet from being part of every app open — and the fallback
 * reserves the space, so nothing jumps when they arrive.
 */
const LazyCharacter = lazy(async () => ({
  default: (await import('@/characters/Character')).Character,
}));

const ART_SIZE = 120;

export function EmptyState({
  title,
  body,
  action,
  art,
  character,
  emotion = 'thinking',
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  art?: ReactNode;
  /**
   * Who keeps the learner company here. An empty screen is the one place the
   * app has nothing to offer, which is exactly where a face helps — so every
   * empty state names the character whose part of the app it belongs to.
   */
  character?: CharacterId;
  emotion?: Emotion;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      {character !== undefined ? (
        <Suspense fallback={<span aria-hidden style={{ width: ART_SIZE, height: ART_SIZE }} />}>
          <LazyCharacter character={character} emotion={emotion} size={ART_SIZE} decorative />
        </Suspense>
      ) : art ? (
        <div aria-hidden>{art}</div>
      ) : null}
      <h2 className="text-xl">{title}</h2>
      {body ? <p className="max-w-sm text-text-2">{body}</p> : null}
      {action}
    </div>
  );
}

/* ---------------------------------------------------------------- ListRow */

export function ListRow({
  leading,
  title,
  subtitle,
  trailing,
  onClick,
}: {
  leading?: ReactNode;
  title: string;
  subtitle?: string;
  trailing?: ReactNode;
  onClick?: () => void;
}) {
  const inner = (
    <>
      {leading ? <span className="shrink-0">{leading}</span> : null}
      <span className="flex min-w-0 flex-1 flex-col text-left">
        <span className="truncate font-semibold">{title}</span>
        {subtitle ? <span className="truncate text-sm text-text-2">{subtitle}</span> : null}
      </span>
      {trailing ? <span className="shrink-0">{trailing}</span> : null}
    </>
  );

  const classes = 'flex min-h-14 w-full items-center gap-3 rounded-lg px-3 py-2 text-left';

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={cn(classes, 'hover:bg-bg-2 active:scale-[0.99] transition-transform')}
    >
      {inner}
    </button>
  ) : (
    <div className={classes}>{inner}</div>
  );
}

/* --------------------------------------------------------------- StatCard */

export function StatCard({
  label,
  value,
  icon,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  tone?: 'neutral' | 'streak' | 'gem' | 'xp' | 'heart';
}) {
  // Ink shades, not the raw tones: gem cyan and XP gold are under 2:1 on a
  // light surface, and a number is text however large it is set.
  const tones = {
    neutral: 'text-text',
    streak: 'text-streak-ink',
    gem: 'text-gem-ink',
    xp: 'text-xp-ink',
    heart: 'text-heart-ink',
  } as const;

  return (
    <div className="flex flex-col gap-1 rounded-lg border-2 border-line bg-surface p-3">
      <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-text-2">
        {icon ? <span aria-hidden>{icon}</span> : null}
        {label}
      </span>
      <span className={cn('text-2xl display-num', tones[tone])}>{value}</span>
    </div>
  );
}
